import React, { useState, useRef, useEffect } from 'react'
import { HelpCircle, X, Play, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import { ALL_TOURS, resetAllTours } from '../config/tourSteps'
import { forceTour } from '../hooks/usePageTour'

/**
 * Botón de ayuda con menú. Props: tourId (clave en ALL_TOURS), id (HTML id para anclas del tour).
 */
export default function HelpButton({ tourId, id, className = '' }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const meta = ALL_TOURS[tourId]
  const sectionLabel = meta?.label || 'esta sección'

  const handleStartTour = () => {
    forceTour(tourId)
    setOpen(false)
  }

  const handleResetAllTours = (e) => {
    e.preventDefault()
    e.stopPropagation()
    resetAllTours()
    toast.success('Progreso de tours reiniciado. Los verás al entrar de nuevo en cada sección.')
    setOpen(false)
    forceTour(tourId)
  }

  return (
    <div
      ref={wrapRef}
      id={id}
      className={`pdf-signer-hb-wrap ${className}`.trim()}
    >
      <style>{`
        .pdf-signer-hb-wrap {
          position: fixed;
          right: 20px;
          bottom: 24px;
          z-index: 50;
          display: inline-flex;
          flex-direction: column-reverse;
          align-items: flex-end;
          gap: 0;
          font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .pdf-signer-hb-fab {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 52px;
          height: 52px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: #1d4ed8;
          color: #ffffff;
          cursor: pointer;
          box-shadow:
            0 4px 14px rgba(29, 78, 216, 0.45),
            0 2px 6px rgba(15, 23, 42, 0.2);
          transition: box-shadow 0.2s ease, transform 0.15s ease, background 0.2s ease;
        }
        .pdf-signer-hb-fab:hover {
          background: #1e40af;
          box-shadow:
            0 6px 20px rgba(29, 78, 216, 0.5),
            0 2px 8px rgba(15, 23, 42, 0.22);
          transform: scale(1.03);
        }
        .pdf-signer-hb-fab:focus-visible {
          outline: 2px solid #93c5fd;
          outline-offset: 3px;
        }
        .pdf-signer-hb-panel {
          margin-bottom: 12px;
          width: 300px;
          max-width: min(300px, calc(100vw - 40px));
          background: #ffffff;
          border-radius: 16px;
          box-shadow:
            0 4px 24px rgba(15, 23, 42, 0.08),
            0 12px 40px rgba(15, 23, 42, 0.1);
          border: 1px solid #f1f5f9;
          overflow: hidden;
        }
        .pdf-signer-hb-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .pdf-signer-hb-head-title {
          font-size: 0.6875rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #94a3b8;
        }
        .pdf-signer-hb-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          padding: 0;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
        }
        .pdf-signer-hb-close:hover {
          background: #f1f5f9;
          color: #64748b;
        }
        .pdf-signer-hb-close:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        .pdf-signer-hb-body {
          padding: 8px 12px 12px;
        }
        .pdf-signer-hb-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          width: 100%;
          text-align: left;
          padding: 12px 12px;
          margin: 0 0 8px 0;
          border: none;
          border-radius: 14px;
          background: transparent;
          cursor: pointer;
          transition: background 0.15s ease;
          font: inherit;
        }
        .pdf-signer-hb-row:last-of-type {
          margin-bottom: 0;
        }
        .pdf-signer-hb-row:hover {
          background: #f8fafc;
        }
        .pdf-signer-hb-row:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 0;
        }
        .pdf-signer-hb-ico {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }
        .pdf-signer-hb-ico--blue {
          background: #eff6ff;
          color: #2563eb;
        }
        .pdf-signer-hb-ico--purple {
          background: #f5f3ff;
          color: #7c3aed;
        }
        .pdf-signer-hb-row-txt {
          flex: 1;
          min-width: 0;
        }
        .pdf-signer-hb-row-title {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #334155;
          line-height: 1.35;
        }
        .pdf-signer-hb-row-sub {
          display: block;
          margin-top: 4px;
          font-size: 0.75rem;
          font-weight: 400;
          color: #64748b;
          line-height: 1.4;
        }
        .pdf-signer-hb-foot {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px 14px;
          background: #f8fafc;
          border-top: 1px solid #f1f5f9;
        }
        .pdf-signer-hb-key {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 26px;
          height: 22px;
          padding: 0 7px;
          border-radius: 4px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 0 #f1f5f9;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          line-height: 1;
        }
        .pdf-signer-hb-foot-txt {
          font-size: 0.75rem;
          color: #94a3b8;
          line-height: 1.35;
        }
      `}</style>
      <button
        type="button"
        className="pdf-signer-hb-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Ayuda"
        aria-label="Ayuda"
      >
        <HelpCircle size={26} strokeWidth={2} aria-hidden />
      </button>
      {open && (
        <div className="pdf-signer-hb-panel" role="menu" aria-label="Ayuda">
          <div className="pdf-signer-hb-head">
            <span className="pdf-signer-hb-head-title">AYUDA</span>
            <button
              type="button"
              className="pdf-signer-hb-close"
              onClick={() => setOpen(false)}
              aria-label="Cerrar ayuda"
              title="Cerrar"
            >
              <X size={18} strokeWidth={2} />
            </button>
          </div>
          <div className="pdf-signer-hb-body">
            <button
              type="button"
              role="menuitem"
              className="pdf-signer-hb-row"
              onClick={handleStartTour}
            >
              <span className="pdf-signer-hb-ico pdf-signer-hb-ico--blue" aria-hidden>
                <Play size={18} strokeWidth={2.25} />
              </span>
              <span className="pdf-signer-hb-row-txt">
                <span className="pdf-signer-hb-row-title">Ver tour de esta sección</span>
                <span className="pdf-signer-hb-row-sub">{sectionLabel}</span>
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="pdf-signer-hb-row"
              onClick={handleResetAllTours}
            >
              <span className="pdf-signer-hb-ico pdf-signer-hb-ico--purple" aria-hidden>
                <RotateCcw size={20} strokeWidth={2} />
              </span>
              <span className="pdf-signer-hb-row-txt">
                <span className="pdf-signer-hb-row-title">Resetear todos los tours</span>
                <span className="pdf-signer-hb-row-sub">Los verás de nuevo al visitar cada sección</span>
              </span>
            </button>
          </div>
          <div className="pdf-signer-hb-foot">
            <kbd className="pdf-signer-hb-key" aria-hidden>
              ?
            </kbd>
            <span className="pdf-signer-hb-foot-txt">para abrir esta ayuda</span>
          </div>
        </div>
      )}
    </div>
  )
}
